import { Component, OnInit, OnDestroy, HostListener, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { SessionsModule } from '../../modules/besharp-sessions/besharp-sessions.module';
import { BesharpSidebarComponent, BesharpSidebarConfig } from '../../modules/besharp-sidebar/component/besharp-sidebar.component';
import { BesharpTopbarComponent, BesharpTopbarItems, mobileMenuListItem } from '../../modules/besharp-topbar/besharp-topbar.component';
import { BesharpBreadcrumbsComponent } from "../../modules/besharp-breadcrumbs/besharp-breadcrumbs.component";
import { ThemeService } from '../../services/theme.service';
import { SidebarService } from '../../services/sidebar.service';
import { Subscription } from 'rxjs';

const MOBILE_BREAKPOINT = 768;

@Component({
  selector: 'app-base-layout',
  templateUrl: './base-layout.component.html',
  styleUrl: './base-layout.component.scss',
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    MenuModule,
    BesharpSidebarComponent,
    BesharpTopbarComponent,
    SessionsModule,
    DividerModule,
    SelectModule,
    BesharpBreadcrumbsComponent,
  ],
})
export class BaseLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  // Topbar Settings
  protected title = 'RDP Client';
  protected logo = '/logo.png';
  protected homeRoute = '/app/home';
  protected topBarItems!: BesharpTopbarItems;
  protected mobileMenu = true;
  protected mobileMenuListItems!: mobileMenuListItem[];

  // Sidebar Settings
  protected sidebarConfiguration!: BesharpSidebarConfig;

  // Internal Values
  protected backgroundImage = '/background-top.png';

  collapsed = false;
  isMobile = false;
  private themeSubscription!: Subscription;

  @ViewChild('sidebarRef') sidebarComponent!: BesharpSidebarComponent;

  constructor(private router: Router, private themeService: ThemeService, private sidebarService: SidebarService) {}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize(event.target.innerWidth);
  }

  ngOnInit(): void {

    this.checkScreenSize(window.innerWidth);
    
    this.updateTopBarItems();

    this.sidebarConfiguration = {
      items: [
        { label: 'Sections', icon: 'pi pi-table', category: true },
        { label: 'Machines', command: () => this.router.navigate(['app', 'machine-list']) },
        { label: 'Pending Sessions', command: () => this.router.navigate(['app', 'pending-sessions']) },
        { label: 'Session History', command: () => this.router.navigate(['app', 'session-history']) },
        { divider: true },
        { spacer: true },
      ],
    };

    this.mobileMenuListItems = [
      { icon: 'pi pi-list', label: 'Machines', command: () => this.router.navigate(['app', 'machine-list']) },
      { icon: 'pi pi-upload', label: 'Pending Sessions', command: () => this.router.navigate(['app', 'pending-sessions']) },
      { icon: 'pi pi-history', label: 'Session History', command: () => this.router.navigate(['app', 'session-history']) },
    ];

    this.themeSubscription = this.themeService.darkMode$.subscribe(() => {
      this.updateTopBarItems();
    });
  }

  ngAfterViewInit(): void {
    if (this.sidebarComponent) {
      this.sidebarService.setSidebarRef(this.sidebarComponent);
    }
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  private updateTopBarItems(): void {
    const isDarkMode = this.themeService.getDarkMode();
    this.topBarItems = {
      itemsSize: 'xs',
      items: [
        {
          icon: isDarkMode ? 'pi pi-moon' : 'pi pi-sun',
          iconPosition: 'left',
          outlined: true,
          command: () => this.toggleDarkMode(),
          severity: 'success',
        },
        { icon: 'pi pi-cog', iconPosition: 'left', outlined: true, command: () => {}, severity: 'danger' },
      ],
    };
  }

  toggleDarkMode(): void {
    this.themeService.toggleDarkMode();
  }

  onSidebarCollapseChanged(collapsed: boolean) {
    this.collapsed = collapsed;
  }

  private checkScreenSize(width: number): void {
    this.isMobile = width <= MOBILE_BREAKPOINT;
  }
}
